import React, { useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import {
  ElementarySchoolApps,
  ElementarySchoolSection,
} from 'src/components/elementary-school-view';
import { SECTIONS_TRANSITION_TIMEOUT } from 'src/constants/animation.constants';
import {
  usePastWorkAssignmentListData,
  useToDoAssignmentListData,
} from 'src/hooks/assignment-list-data';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useGetProductsByEntitlement } from 'src/hooks/use-get-products-by-entitlement.hook';
import { AssignmentsViewOptions } from 'src/interfaces/assignment.types';
import authService from 'src/services/api/auth.service';
import {
  setActiveAssignmentsView,
  setAssignmentsTotalCount,
  setDisplayedAssignmentsCount,
  setDoneAssignmentsCount,
  useActiveAssignmentsViewSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';
import { getDoneAssignments } from 'src/utils/xapi.utils';

export const ElementarySchoolSections = () => {
  const activeAssignmentsView = useActiveAssignmentsViewSelector();
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();
  const isTeacher = authService.isTeacher();
  const {
    itemsToDisplay: itemsToDisplayToDo,
    incrementItemsToDisplayCount: incrementItemsToDisplayCountToDo,
    isLoading: isToDoAssignmentsLoading,
    isFetching: isFetchingToDo,
    allAssignmentCount: allAssignmentCountToDo,
  } = useToDoAssignmentListData();
  const {
    itemsToDisplay: itemsToDisplayPW,
    incrementItemsToDisplayCount: incrementItemsToDisplayCountPW,
    isLoading: isLoadingPW,
    isFetching: isFetchingPW,
    allAssignmentCount: allAssignmentCountPW,
  } = usePastWorkAssignmentListData();
  const { productsByEntitlement } = useGetProductsByEntitlement();
  const toDoSectionRef = useRef<HTMLDivElement>(null);
  const pastWorkSectionRef = useRef<HTMLDivElement>(null);
  const prevViewRef = useRef<AssignmentsViewOptions>(
    AssignmentsViewOptions.TODO,
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    return () => {
      prevViewRef.current = activeAssignmentsView;
    };
  }, [activeAssignmentsView]);
  // Set assignments xAPI payload data.
  useEffect(() => {
    if (activeAssignmentsView === AssignmentsViewOptions.TODO) {
      dispatch(setDisplayedAssignmentsCount(itemsToDisplayToDo.length));
      dispatch(setAssignmentsTotalCount(allAssignmentCountToDo ?? 0));
      dispatch(setDoneAssignmentsCount(0));
    }
    if (activeAssignmentsView === AssignmentsViewOptions.PAST_WORK) {
      dispatch(setDisplayedAssignmentsCount(itemsToDisplayPW.length));
      dispatch(setAssignmentsTotalCount(allAssignmentCountPW ?? 0));
      const doneAssignments = getDoneAssignments(itemsToDisplayPW);
      dispatch(setDoneAssignmentsCount(doneAssignments.length));
    }
  }, [
    activeAssignmentsView,
    allAssignmentCountPW,
    allAssignmentCountToDo,
    dispatch,
    itemsToDisplayPW,
    itemsToDisplayToDo.length,
  ]);

  useEffect(() => {
    if (isTeacher || isToDoAssignmentsLoading) return;
    // Switch to the 'Apps' section instead of showing an empty 'To-Do' one.
    if (
      !itemsToDisplayToDo.length &&
      prevViewRef.current === AssignmentsViewOptions.TODO &&
      activeAssignmentsView !== AssignmentsViewOptions.PAST_WORK &&
      productsByEntitlement.length
    ) {
      dispatch(setActiveAssignmentsView(AssignmentsViewOptions.APPS));
    }
  }, [
    isToDoAssignmentsLoading,
    itemsToDisplayToDo,
    activeAssignmentsView,
    dispatch,
    isTeacher,
    productsByEntitlement,
  ]);

  const isRightForApps = activeAssignmentsView === AssignmentsViewOptions.APPS;
  const isLeftForPastWork =
    activeAssignmentsView === AssignmentsViewOptions.PAST_WORK;

  const isRightForToDo =
    activeAssignmentsView === AssignmentsViewOptions.TODO
      ? prevViewRef.current === AssignmentsViewOptions.PAST_WORK
      : activeAssignmentsView === AssignmentsViewOptions.APPS;

  return (
    <>
      <ElementarySchoolApps
        animationIn={activeAssignmentsView === AssignmentsViewOptions.APPS}
        isSlideRight={isRightForApps}
      />

      <CSSTransition
        classNames={isRightForToDo ? 'slide-right' : 'slide-left'}
        timeout={SECTIONS_TRANSITION_TIMEOUT}
        unmountOnExit
        nodeRef={toDoSectionRef}
        in={activeAssignmentsView === AssignmentsViewOptions.TODO}
      >
        <ElementarySchoolSection
          sectionRef={toDoSectionRef}
          isAssignmentDataLoading={isToDoAssignmentsLoading}
          isAssignmentDataFetching={isFetchingToDo}
          itemsToDisplay={itemsToDisplayToDo}
          incrementItemsToDisplayCount={incrementItemsToDisplayCountToDo}
          commonXapiContextPayload={commonXapiContextPayload}
          section={AssignmentsViewOptions.TODO}
        />
      </CSSTransition>

      <CSSTransition
        unmountOnExit
        classNames={isLeftForPastWork ? 'slide-left' : 'slide-right'}
        timeout={SECTIONS_TRANSITION_TIMEOUT}
        nodeRef={pastWorkSectionRef}
        in={activeAssignmentsView === AssignmentsViewOptions.PAST_WORK}
      >
        <ElementarySchoolSection
          sectionRef={pastWorkSectionRef}
          isAssignmentDataLoading={isLoadingPW}
          isAssignmentDataFetching={isFetchingPW}
          itemsToDisplay={itemsToDisplayPW}
          incrementItemsToDisplayCount={incrementItemsToDisplayCountPW}
          commonXapiContextPayload={commonXapiContextPayload}
          section={AssignmentsViewOptions.PAST_WORK}
        />
      </CSSTransition>
    </>
  );
};
